import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import {saveCurrentStep} from "@/composables/ai-onboarding/useSteps";
import SweetToast from "@/SweetToast";
import {useStore} from "vuex";
import {computed, ref} from "vue";

// Sentinel used to silently discard a duplicate completion attempt without
// surfacing an error toast to the user.
class DuplicateCompletionError extends Error {}

// Module-level lock shared across every component instance. Completion creates
// a KitSchedule server-side via a non-atomic firstOrCreate, so allowing it to
// fire twice (double-click, or multiple code paths triggering it) can produce
// duplicate schedule rows. This guarantees the completion requests run once.
const isCompletingOnboarding = ref(false);

const useCompleteOnboarding = () => {
  const store = useStore();

  const user = computed(() => store.getters['user/user']);

  const completeOnboardingMutation = useMutation({
    mutationFn: async () => {
      // Check-and-set is synchronous (no await in between), so concurrent
      // mutate() calls in the same tick cannot both pass this guard.
      if (isCompletingOnboarding.value) {
        throw new DuplicateCompletionError();
      }

      isCompletingOnboarding.value = true;

      await axios.post('/ajax/v2/ai-onboarding/keywords/track');
      await saveCurrentStep({key: 'completed'});
    },
    onSuccess: () => {
      window.mixpanel('Onboarding: Complete Onboarding', {
        website_id: user.value?.active_website?.id,
      });

      SweetToast.fire({
        icon: "success",
        title: 'Onboarding complete',
      });

      window.location.href = '/dash';
    },
    onError: (error) => {
      if (error instanceof DuplicateCompletionError) {
        return;
      }

      // Real failure - release the lock so the user can retry.
      isCompletingOnboarding.value = false;

      if (error.response?.status === 400) {
        SweetToast.fire({
          icon: 'error',
          title: error.response.data.message,
        });
      }

      throw new Error('Something went wrong');
    },
  });

  return {
    completeOnboardingMutation,
    isCompletingOnboarding,
  };
};

export default useCompleteOnboarding;
